import React, { useEffect, useRef, useState, useCallback } from 'react';
import './FluidSimulation.css';
import Navbar from '../Navbar/Navbar';
import HeroSection from '../HeroSection/HeroSection';
import ProjectsCarousel from '../ProjectsCarousel/ProjectsCarousel';
import Services from '../Services/Services';
import Products from '../Products/Products.jsx';
import ContactForm from '../ContactForm/ContactForm';
import SocialMediaGrid from '../SocialMediaGrid/SocialMediaGrid';
import Footer from '../../components/Footer/Footer';
import ParticleBackground from '../../components/ParticleBackground/ParticleBackground';

const FluidSimulation = () => {
  const [scriptError, setScriptError] = useState(null);
  const [particlesReady, setParticlesReady] = useState(false);
  const [componentMounted, setComponentMounted] = useState(false);
  const canvasRef = useRef(null);
  
  // Check WebGL availability
  const checkWebGL = () => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      return !!gl;
    } catch (error) {
      return false;
    }
  };

  const [webGLAvailable] = useState(checkWebGL());



  
  useEffect(() => {
    console.log("FluidSimulation component mounted");
    setComponentMounted(true);
  }, []);

  // Add Spline script to the document
  useEffect(() => {
    const splineScript = document.createElement('script');
    splineScript.type = 'module';
    splineScript.src = 'https://unpkg.com/@splinetool/viewer@1.9.82/build/spline-viewer.js';
    document.head.appendChild(splineScript);
    
    return () => {
      document.head.removeChild(splineScript);
    };
  }, []);
  
  // Create many blobs positioned far above the screen
  useEffect(() => {
    const pageDecoration = document.querySelector('.page-decoration');
    if (!pageDecoration) return;
    
    // Create a blob with specific vertical position and horizontal bias
    const createBlob = (verticalPosition, horizontalBias = null) => {
      const blob = document.createElement('div');
      blob.className = 'floating-blob';
      
      // Randomize horizontal position with optional bias
      let leftPosition;
      if (horizontalBias === 'far-left') {
        leftPosition = -5 + Math.random() * 15; // -5% to 10% (far left edge)
      } else if (horizontalBias === 'left') {
        leftPosition = 10 + Math.random() * 20; // 10% to 30% (left side)
      } else if (horizontalBias === 'center') {
        leftPosition = 30 + Math.random() * 40; // 30% to 70% (center)
      } else if (horizontalBias === 'right') {
        leftPosition = 70 + Math.random() * 20; // 70% to 90% (right side)
      } else if (horizontalBias === 'far-right') {
        leftPosition = 90 + Math.random() * 15; // 90% to 105% (far right edge)
      } else {
        leftPosition = 5 + Math.random() * 90; // 5% to 95% (full width)
      }
      
      blob.style.left = `${leftPosition}%`;
      
      // Set vertical position (negative to be above the viewport)
      blob.style.top = `${verticalPosition}px`;
      
      // Randomize size
      const size = 100 + Math.random() * 200; // Random size between 100px and 300px
      blob.style.width = `${size}px`;
      blob.style.height = `${size}px`;
      
      // Randomize opacity and color intensity
      const opacity = 0.25 + Math.random() * 0.15; // Between 0.25 and 0.4
      blob.style.background = `rgba(227, 41, 97, ${opacity})`;
      
      // Set parallax speed
      blob.dataset.speed = 0.2 + Math.random() * 0.2; // Random speed between 0.2 and 0.4
      
      return blob;
    };
    
    // Create blobs with better distribution
    const totalBlobs = 65;
    const blobs = [];
    
    // Position blobs from -15000px up to the top of the viewport
    // with even distribution across far-left, left, center, right, and far-right
    for (let i = 0; i < totalBlobs; i++) {
      const verticalPosition = -15000 + (i * (15000 / totalBlobs));
      
      // Determine horizontal bias based on position
      let horizontalBias;
      const position = i % 5;
      if (position === 0) horizontalBias = 'far-left';
      else if (position === 1) horizontalBias = 'left';
      else if (position === 2) horizontalBias = 'center';
      else if (position === 3) horizontalBias = 'right';
      else horizontalBias = 'far-right';
      
      const blob = createBlob(verticalPosition, horizontalBias);
      pageDecoration.appendChild(blob);
      blobs.push(blob);
    }
    
    // Add extra blobs near the top with explicit edge bias
    for (let i = 0; i < 15; i++) {
      // Cycle through all positions for the extra blobs
      const positions = ['far-left', 'left', 'center', 'right', 'far-right'];
      const horizontalBias = positions[i % 5];
      const verticalPosition = -500 - (i * 300); // Position between -500px and -4900px
      const blob = createBlob(verticalPosition, horizontalBias);
      pageDecoration.appendChild(blob);
      blobs.push(blob);
    }
    
    // Add specific blobs at the far left edges
    for (let i = 0; i < 5; i++) {
      const verticalPosition = -1000 - (i * 800); // Well-spaced vertical positions
      const blob = createBlob(verticalPosition, 'far-left');
      pageDecoration.appendChild(blob);
      blobs.push(blob);
    }
    
    // Handle scroll to move blobs with parallax effect
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Update all blobs position
      blobs.forEach((blob) => {
        const speed = parseFloat(blob.dataset.speed) || 0.3;
        const initialTop = parseInt(blob.style.top) || 0;
        
        // Apply parallax movement
        blob.style.transform = `translateY(${currentScrollY * speed}px)`;
        
        // Calculate actual position
        const blobTop = initialTop + (currentScrollY * speed);
        
        // Make blobs visible only when they're near or in the viewport
        if (blobTop < -viewportHeight || blobTop > viewportHeight * 2) {
          blob.style.opacity = '0';
        } else {
          // Fade in as they approach the viewport
          const distance = Math.abs(blobTop);
          const maxDistance = viewportHeight;
          // Opacity range: minimum 0.2, maximum 0.5
          const opacity = 0.2 + (0.3 * (1 - Math.min(distance / maxDistance, 1)));
          blob.style.opacity = opacity.toString();
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Call once to set initial positions
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clean up blobs
      blobs.forEach(blob => {
        if (pageDecoration.contains(blob)) {
          pageDecoration.removeChild(blob);
        }
      });
    };
  }, []);
  
  // Manual canvas-based particle system as fallback
  useEffect(() => {
    const createManualParticles = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      const particles = [];
      
      // Create particles
      for (let i = 0; i < 50; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 2,
          vy: (Math.random() - 0.5) * 2,
          size: Math.random() * 3 + 1
        });
      }
      
      // Animation loop
      const animate = () => {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.fillStyle = '#e32961';
        particles.forEach(particle => {
          particle.x += particle.vx;
          particle.y += particle.vy;
          
          if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
          if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;
          
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
        });
        
        requestAnimationFrame(animate);
      };
      
      animate();
      setParticlesReady(true);
      console.log("✅ Manual particles created successfully");
    };
    
    // Try manual particles after a delay
    setTimeout(createManualParticles, 1000);
  }, []);
  
  return (
    <div className="fluid-simulation-container">
      
      {/* Reusable Particle Background */}
      <ParticleBackground 
        backgroundColor="#000000"
        particleColor="#e32961"
        particleCount={100}
        showDebug={false}
      />
      
      {/* Container for dynamically created blobs */}
      <div className="page-decoration"></div>
      
      <div className="content-wrapper">
        <Navbar /> 
        <HeroSection />
        <ProjectsCarousel />
        <Services />
        <Products />
      </div>
        
      <div className="contact-social-section-wrapper">
        <div className="contact-social-section">
          <div className="contact-side">
            <ContactForm />
          </div>
          <div className="social-side">
            <SocialMediaGrid />
          </div>
        </div>
      </div>
        
      <div className="content-wrapper">
        <div className="promo">
          <div className="promo-middle">
            <div className="promo-content">
              <div className="promo-header">
                <span className="promo-close">&times;</span>
              </div>
              <div className="promo-body">
                <p>Try Fluid Simulation app!</p>
                <div className="links-container">
                  <a className="link" id="apple_link" target="_blank" rel="noopener noreferrer">
                    <img className="link-img" alt="Download on the App Store" src="/app_badge.png"/>
                  </a>
                  <a className="link" id="google_link" target="_blank" rel="noopener noreferrer">
                    <img className="link-img" alt="Get it on Google Play" src="/gp_badge.png"/>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>
        {`
          .dg.ac {
            display: none !important;
          }
          
          /* Page-wide blob styling */
          .page-decoration {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            minHeight: 100vh;
            minHeight: 100dvh; /* Better mobile support */
            pointer-events: none;
            z-index: 1;
            overflow: hidden;
          }
          
          /* Ensure particles background covers full screen on all devices */
          .particle-background-container {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            min-height: 100vh !important;
            min-height: 100dvh !important; /* Better mobile support */
            min-height: -webkit-fill-available !important; /* Safari/Mac fix */
            z-index: 0 !important;
            pointer-events: none !important;
          }
          
          .floating-blob {
            position: absolute;
            border-radius: 50%;
            filter: blur(15px);
            box-shadow: 0 0 25px rgba(227, 41, 97, 0.5);
            will-change: transform, opacity;
            transition: opacity 0.5s ease;
          }
          
          /* Make sure content is above the blobs */
          .content-wrapper {
            position: relative;
            z-index: 2;
          }
          
          /* Contact and Social Media section styling */
          .contact-social-section-wrapper {
            width: 100%;
            padding: 0;
            margin: 4rem 0;
            overflow: hidden;
            box-sizing: border-box;
            position: relative;
            z-index: 10; /* Add high z-index to ensure it's above all background elements */
          }
          
          .contact-social-section {
            display: flex;
            flex-direction: row;
            width: 90%; /* Reduced from 100% to create space on both sides */
            max-width: 1800px;
            margin: 0 auto; /* This centers the container, creating equal space on both sides */
            align-items: stretch;
            gap: 4rem;
            padding: 0;
            box-sizing: border-box;
          }
          
          .contact-side {
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: stretch;
            min-width: 0;
            max-width: 50%;
            min-height: 600px;
            padding: 1rem;
            transform-style: preserve-3d;
            perspective: 1000px;
            box-sizing: border-box;
            overflow: hidden;
            position: relative;
            z-index: 15; /* Ensure contact form is above background */
          }
          
          .social-side {
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: stretch;
            min-width: 0;
            max-width: 50%;
            min-height: 600px;
            padding: 1rem;
            transform-style: preserve-3d;
            perspective: 1000px;
            box-sizing: border-box;
            overflow: hidden;
            position: relative;
            z-index: 15; /* Ensure social media grid is above background */
          }
          
          /* Make sure the contact form and social media grid fit within their containers and stretch to full height */
          .contact-side > *, 
          .social-side > * {
            max-width: 100%;
            width: 100%;
            height: 100%;
            min-height: 100%;
            transform-style: preserve-3d;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
          }
          
          /* Ensure all child elements respect container boundaries */
          .contact-side * {
            max-width: 100%;
            box-sizing: border-box;
          }
          
          .social-side * {
            max-width: 100%;
            box-sizing: border-box;
          }
          
          /* Ensure input fields don't get cut off */
          .contact-side input,
          .contact-side textarea,
          .contact-side select,
          .contact-side button {
            max-width: 100%;
            box-sizing: border-box;
            position: relative;
            z-index: 20; /* High z-index for form elements */
            pointer-events: auto; /* Explicitly enable pointer events */
          }
          
          /* Override contact form animation classes to ensure visibility */
          .contact-side .animate-element,
          .contact-side .form-group.animate-element,
          .contact-side .contact-title.animate-element,
          .contact-side .contact-subtitle.animate-element,
          .contact-side .submit-btn.animate-element {
            opacity: 1 !important;
            transform: translateY(0) !important;
          }
          
          /* Ensure contact form inputs are always visible and clickable */
          .contact-side .form-group,
          .contact-side .form-group input,
          .contact-side .form-group textarea,
          .contact-side .form-group label,
          .contact-side .contact-form,
          .contact-side .contact-container {
            opacity: 1 !important;
            visibility: visible !important;
            pointer-events: auto !important;
            z-index: inherit;
          }
          
          /* Responsive layout for mobile */
          @media (max-width: 768px) {
            .contact-social-section {
              flex-direction: column;
              align-items: center;
              gap: 3rem;
              padding: 0.5rem;
              width: 100%;
            }
            
            .contact-side,
            .social-side {
              padding: 0.5rem;
              width: 100%;
              max-width: 500px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            
            .contact-social-section-wrapper {
              padding: 0 1rem;
              margin: 3rem 0;
              width: 100%;
              display: flex;
              justify-content: center;
            }
            
            /* Override child component alignment for mobile */
            .contact-side .contact-section,
            .social-side .social-media-section {
              align-items: center !important;
              justify-content: center !important;
              text-align: center !important;
            }
            
            .contact-side .contact-container,
            .social-side .social-media-container {
              margin: 0 auto !important;
              align-self: center !important;
            }
            
            .contact-side .contact-text,
            .social-side .social-media-text {
              text-align: center !important;
            }
            
            .contact-side .contact-subtitle,
            .social-side .social-media-subtitle {
              max-width: 100% !important;
              text-align: center !important;
            }
          }
          
          /* Mobile-specific fixes for particle background */
          @media screen and (max-width: 768px) {
            .fluid-simulation-container {
              min-height: 100vh;
              min-height: 100dvh; /* Dynamic viewport height for mobile */
              min-height: -webkit-fill-available; /* Safari fallback */
            }
            
            .particle-background-container,
            .page-decoration {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              min-height: 100vh !important;
              min-height: 100dvh !important;
              min-height: -webkit-fill-available !important;
              max-height: none !important;
              z-index: 0 !important;
            }
            
            /* Ensure tsparticles canvas covers full screen */
            #tsparticles {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              min-height: 100vh !important;
              min-height: 100dvh !important;
              min-height: -webkit-fill-available !important;
            }
          }
          
          /* Mac/Safari specific fixes for full viewport coverage */
          @media screen and (-webkit-min-device-pixel-ratio: 1) {
            .particle-background-container,
            .page-decoration {
              height: 100vh !important;
              min-height: 100vh !important;
              min-height: -webkit-fill-available !important;
            }
            
            #tsparticles {
              height: 100vh !important;
              min-height: 100vh !important;
              min-height: -webkit-fill-available !important;
            }
          }
          
          /* Additional Safari-specific fix */
          @supports (-webkit-appearance: none) and (not (contain: paint)) {
            .particle-background-container,
            .page-decoration,
            #tsparticles {
              height: calc(100vh + 1px) !important;
              min-height: calc(100vh + 1px) !important;
            }
          }
        `}
      </style>
      <Footer />
    </div>
  );
};

export default FluidSimulation; 